import { noop } from '@kofno/piper';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import { fromNullable, fromEmpty } from 'maybeasy';
import { memo, useEffect } from 'react';
import Label from '../../../../../../Resource/FormTemplates/Inputs/Label';
import MultipleSelectionsInput from '../../../../../../Resource/FormTemplates/Inputs/MultipleSelectionsInput';
import WithField from '../../../../../../Resource/FormTemplates/Inputs/WithField';
import { findField } from '../../../../../../Resource/FormTemplates/Types';
import { T } from '../../../../../../Translations';
import Button from '../../../../../Form/Button';
import { AddLicenseToParticipantsStore } from '../Store';
import { InitialReady, LicensesReady, FetchingLicenseOptions } from '../Types';
import ProductLicense from './ProductLicense';
import { emptyFragment } from '@execonline-inc/execonline-ui';

interface Props {
  state: InitialReady | FetchingLicenseOptions | LicensesReady;
  submitting: () => void;
  store: AddLicenseToParticipantsStore;
}

function Form({ state, submitting, store }: Props) {
  // Watch for license_action field changes
  const licenseActionValue = findField('license_action', 'single-selection', state.template.fields)
    .andThen(({ value }) => fromNullable(value))
    .map((option) => option.value)
    .getOrElse(() => '');

  // Fetch licenses when the user changes the action selection. state.kind is
  // deliberately not a dep: the state machine transitions back through
  // licenses-ready after each successful fetch (Store.fetchingLicenseOptions
  // permits it so the user can change selection later), so including state.kind
  // re-fires this effect on every transition and causes an infinite fetch loop.
  // store.fetchingLicenseOptions() is a no-op outside the valid states.
  useEffect(() => {
    if (licenseActionValue) {
      store.fetchingLicenseOptions();
    }
  }, [licenseActionValue, store]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (store.canSubmit) {
          submitting();
        }
      }}
    >
      {store.allParticipantsFromSameOrg
        .map(() => (
          <>
            <div className="w-1/2">
              <WithField
                kind="single-selection"
                name="license_action"
                fields={state.template.fields}
              >
                {(field) => (
                  <>
                    <Label field={field} />
                    <MultipleSelectionsInput field={field} />
                  </>
                )}
              </WithField>
            </div>
            <ProductLicense state={state} store={store} />
          </>
        ))
        .getOrElse(() => (
          <p
            className={clsx(
              'mt-4 rounded border-1 border-solid border-red-400 bg-red-100 px-4 py-3',
              'text-sm text-red-800',
            )}
          >
            <T kind="All participants must belong to the same org when bulk license provisioning" />
          </p>
        ))}
      {fromEmpty(licenseActionValue)
        .map((action) => (
          <p
            className={clsx(
              'mt-4  rounded border-1 border-solid border-teal-400 bg-teal-500 px-4 py-3',
              'text-sm text-gray-800',
            )}
          >
            {action === 'allocate' && (
              <T kind="After allocating, remember to invite participants to..." />
            )}
            {action === 'release' && (
              <T kind="Activated licenses are ineligible to release. After releasing..." />
            )}
          </p>
        ))
        .getOrElse(emptyFragment)}
      <Button
        type="submit"
        value="Submit"
        onClick={noop}
        disabled={!store.canSubmit}
        data-intercom-target="add-license-to-participants-submit"
        className={clsx(
          'rounded-md border border-solid border-gray-900 bg-white',
          'py-2.5 px-5 text-sm font-semibold text-gray-900 mt-12',
          !store.canSubmit && 'opacity-50 cursor-not-allowed',
        )}
      />
    </form>
  );
}

export default memo(observer(Form));
